import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Mail, User, Key, Eye, Copy, Check, RefreshCw, Loader2, CheckCircle, AlertCircle, Clock, History, Send, MailCheck } from 'lucide-react';
import useInvitationDialog from './useInvitationDialog';
import InvitationFormTab from './InvitationFormTab';
import DeliveryStatusBadges from './DeliveryStatusBadges';
import BounceAlertBanner from './BounceAlertBanner';

const InvitationDialog = ({ isOpen, onClose, onSuccess, prefilledRole = null }) => {
  const ctx = useInvitationDialog({ isOpen, prefilledRole, onSuccess });
  const {
    activeTab, setActiveTab,
    loading, previewLoading,
    emailPreview, invitationResult,
    copiedField, historique, loadingHistorique,
    formData,
    loadPreview, loadHistorique, handleSubmit, handleResend,
    copyToClipboard, getRoleInfo, resetForm,
    checkingDelivery, handleCheckDelivery, handleCorrigerRenvoyer,
  } = ctx;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Mail className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <span className="text-xl">Invitation par Email</span>
              <p className="text-sm font-normal text-gray-500 mt-1">
                Créer un compte et envoyer les identifiants
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="formulaire" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Formulaire
            </TabsTrigger>
            <TabsTrigger value="apercu" className="flex items-center gap-2" disabled={!formData.prenom}>
              <Eye className="h-4 w-4" />
              Aperçu
            </TabsTrigger>
            <TabsTrigger value="resultat" className="flex items-center gap-2" disabled={!invitationResult}>
              <CheckCircle className="h-4 w-4" />
              Résultat
            </TabsTrigger>
            <TabsTrigger value="historique" className="flex items-center gap-2" onClick={loadHistorique}>
              <History className="h-4 w-4" />
              Historique
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto p-1">
            {/* Formulaire */}
            <InvitationFormTab {...ctx} />

            {/* Aperçu Email */}
            <TabsContent value="apercu" className="mt-4">
              {emailPreview ? (
                <div className="space-y-4">
                  <div className="bg-slate-100 p-3 rounded-lg">
                    <p className="text-sm text-gray-500">Sujet :</p>
                    <p className="font-medium">{emailPreview.subject}</p>
                  </div>
                  <div
                    className="border rounded-lg overflow-hidden bg-white"
                    style={{ maxHeight: '400px', overflowY: 'auto' }}
                  >
                    <iframe
                      srcDoc={emailPreview.body_html}
                      title="Aperçu email"
                      className="w-full h-[400px] border-0"
                    />
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Eye className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">Cliquez sur &quot;Prévisualiser&quot; pour voir l&apos;email</p>
                  <Button
                    onClick={loadPreview}
                    className="mt-4"
                    disabled={previewLoading}
                  >
                    {previewLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Eye className="h-4 w-4 mr-2" />
                    )}
                    Prévisualiser l&apos;email
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* Résultat */}
            <TabsContent value="resultat" className="mt-4">
              {invitationResult && (
                <div className="space-y-4">
                  <div className="text-center py-6">
                    <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-green-800">Invitation créée avec succès !</h3>
                    <p className="text-gray-500 mt-2">
                      {invitationResult.email_envoye
                        ? "L'email d'invitation a été envoyé"
                        : "Le compte a été créé (email non envoyé)"}
                    </p>
                  </div>

                  <Card className="border-2 border-blue-200">
                    <CardContent className="p-6">
                      <h4 className="font-semibold mb-4 flex items-center gap-2">
                        <Key className="h-5 w-5 text-blue-600" />
                        Identifiants de connexion
                      </h4>

                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="text-sm text-gray-500">Email / Identifiant</p>
                            <p className="font-mono font-semibold">{invitationResult.identifiant}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(invitationResult.identifiant, 'email')}
                          >
                            {copiedField === 'email' ? (
                              <Check className="h-4 w-4 text-green-600" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </Button>
                        </div>

                        <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="text-sm text-gray-500">Mot de passe temporaire</p>
                            <p className="font-mono font-semibold">{invitationResult.mot_de_passe}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(invitationResult.mot_de_passe, 'password')}
                          >
                            {copiedField === 'password' ? (
                              <Check className="h-4 w-4 text-green-600" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </div>

                      <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-sm text-amber-800">
                          <AlertCircle className="h-4 w-4 inline mr-2" />
                          Conservez ces identifiants. L&apos;utilisateur devra changer son mot de passe à la première connexion.
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="flex gap-3 justify-center">
                    <Button variant="outline" onClick={resetForm}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Nouvelle invitation
                    </Button>
                    <Button onClick={onClose}>
                      Terminer
                    </Button>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Historique */}
            <TabsContent value="historique" className="mt-4">
              {loadingHistorique ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : historique.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Clock className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>Aucune invitation envoyée</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <BounceAlertBanner
                    bounced={historique.filter(i => i.email_sent && i.delivery?.bounced)}
                    onCorrigerRenvoyer={handleCorrigerRenvoyer}
                  />
                  {historique.map((inv) => {
                    const roleInfo = getRoleInfo(inv.role);
                    return (
                      <div
                        key={inv.id}
                        className="flex items-center justify-between p-4 bg-white border rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg bg-${roleInfo.color}-100`}>
                            <roleInfo.icon className={`h-5 w-5 text-${roleInfo.color}-600`} />
                          </div>
                          <div>
                            <p className="font-medium">{inv.prenom} {inv.nom}</p>
                            <p className="text-sm text-gray-500">{inv.email}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">
                                {roleInfo.label}
                              </Badge>
                              <span className="text-xs text-gray-400">
                                {new Date(inv.created_at).toLocaleDateString('fr-FR')}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {inv.email_sent ? (
                            <DeliveryStatusBadges delivery={inv.delivery} />
                          ) : (
                            <Badge variant="outline">Non envoyé</Badge>
                          )}
                          {inv.email_sent && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCheckDelivery(inv.id)}
                              disabled={checkingDelivery === inv.id}
                              title="Vérifier la réception (Brevo)"
                              data-testid={`invitation-check-delivery-${inv.id}`}
                            >
                              {checkingDelivery === inv.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <MailCheck className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResend(inv.id)}
                            title="Renvoyer l'invitation"
                            data-testid={`invitation-resend-${inv.id}`}
                          >
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>
          </div>
        </Tabs>

        <DialogFooter className="border-t pt-4">
          {activeTab === 'formulaire' && (
            <>
              <Button variant="outline" onClick={onClose}>
                Annuler
              </Button>
              <Button variant="outline" onClick={loadPreview} disabled={!formData.prenom || !formData.nom}>
                <Eye className="h-4 w-4 mr-2" />
                Prévisualiser
              </Button>
              <Button onClick={handleSubmit} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Envoyer l&apos;invitation
              </Button>
            </>
          )}
          {activeTab === 'apercu' && (
            <>
              <Button variant="outline" onClick={() => setActiveTab('formulaire')}>
                Retour
              </Button>
              <Button onClick={handleSubmit} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Confirmer et envoyer
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InvitationDialog;
